import { useCallback, useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Box, Text, Title } from '@factorialco/deprecated-design-system'
import {
  CardSelectableContainer,
  type CardSelectableItem,
} from '@factorialco/f0-react/dist/experimental'
import { ReusableMileagesDocument, ReusableMileagesQuery } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { formatStoredMileage } from 'modules/expenses/lib/numberFormating'

type ReusableMileage = ReusableMileagesQuery['expenses']['expensablesConnection']['nodes'][number]

interface ReuseMileageListProps {
  selectedMileageId: number | undefined
  onSelectionChange: (mileage: ReusableMileage | undefined) => void
}

const REUSABLE_MILEAGES_LIMIT = 6

const buildMileageTitle = (mileage: ReusableMileage): string => {
  const from = mileage.mileage?.from ?? ''
  const to = mileage.mileage?.to ?? ''
  const distance = mileage.mileage?.mileage
  const units = mileage.mileage?.units ?? 'km'
  const stops = mileage.mileage?.mileageStops ?? []

  const formattedDistance = distance != null ? formatStoredMileage(distance) : ''
  const distanceLabel = formattedDistance !== '' ? ` - ${formattedDistance} ${units}` : ''

  let routeLabel: string
  if (stops.length === 1) {
    const stopName = stops[0]?.name ?? ''
    routeLabel = `${from} \u2192 ${stopName} \u2192 ${to}`
  } else if (stops.length > 1) {
    routeLabel = `${from} \u2192 ${i18n.t('expenses_v2.forms.new_form.reuse_trip.stops', {
      count: stops.length,
    })} \u2192 ${to}`
  } else {
    routeLabel = `${from} \u2192 ${to}`
  }

  return `${routeLabel}${distanceLabel}`
}

const ReuseMileageList = ({ selectedMileageId, onSelectionChange }: ReuseMileageListProps) => {
  const { company, employee } = useCurrentSession()
  const [searchQuery, setSearchQuery] = useState('')

  const { data, loading } = useQuery(ReusableMileagesDocument, {
    variables: {
      companyId: company.id,
      employeeIds: employee?.id ? [employee.id] : [],
      perPage: REUSABLE_MILEAGES_LIMIT,
    },
    fetchPolicy: 'cache-and-network',
  })

  const mileages = useMemo(
    () => data?.expenses?.expensablesConnection?.nodes ?? [],
    [data?.expenses?.expensablesConnection?.nodes]
  )

  const filteredMileages = useMemo(() => {
    if (!searchQuery.trim()) return mileages

    const query = searchQuery.toLowerCase()
    return mileages.filter((m) => {
      const from = m.mileage?.from?.toLowerCase() ?? ''
      const to = m.mileage?.to?.toLowerCase() ?? ''
      const description = m.description?.toLowerCase() ?? ''
      return from.includes(query) || to.includes(query) || description.includes(query)
    })
  }, [mileages, searchQuery])

  const cardItems: CardSelectableItem<number>[] = useMemo(
    () =>
      filteredMileages.map((mileage) => ({
        value: mileage.id,
        title: buildMileageTitle(mileage),
        description: mileage.description ?? undefined,
      })),
    [filteredMileages]
  )

  const handleChange = useCallback(
    (value: number | undefined) => {
      if (value == null) {
        onSelectionChange(undefined)
        return
      }
      const mileage = filteredMileages.find((m) => m.id === value)
      onSelectionChange(mileage)
    },
    [filteredMileages, onSelectionChange]
  )

  return (
    <Box gap="s16" padding="s24">
      <Box gap="s4">
        <Title variant="400" title={i18n.t('expenses_v2.forms.new_form.reuse_trip.title')} />
        <Text.Normal size="200" color="grey800">
          {i18n.t('expenses_v2.forms.new_form.reuse_trip.subtitle')}
        </Text.Normal>
      </Box>

      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={i18n.t('expenses_v2.forms.new_form.reuse_trip.search_placeholder')}
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: '8px',
          border: '1px solid var(--color-grey-300, #d0d5dd)',
          fontSize: '14px',
          boxSizing: 'border-box',
        }}
      />

      {loading && !data && (
        <Box padding="s16" justifyContent="center" alignItems="center">
          <Text.Normal size="200" color="grey600">
            ...
          </Text.Normal>
        </Box>
      )}

      {!loading && filteredMileages.length === 0 && (
        <Box padding="s16" justifyContent="center" alignItems="center">
          <Text.Normal size="200" color="grey600">
            {i18n.t('expenses_v2.forms.new_form.reuse_trip.empty')}
          </Text.Normal>
        </Box>
      )}

      {cardItems.length > 0 && (
        <CardSelectableContainer
          items={cardItems}
          value={selectedMileageId}
          onChange={handleChange}
        />
      )}
    </Box>
  )
}

export default ReuseMileageList
export type { ReusableMileage }
